import { formatTime } from '../../../utils';
import { State } from '../../../storeavailability';

const uiLocale = require('../../../utils/UILocale.js');
const uiSettings = require('../../../utils/UISettings.js');

export const REFRESH_INTERVAL = 30000;

export function formatOpeningHours(day, translationsPrefix) {
  if (typeof day.openMinutes !== 'number') {
    return uiLocale.getString(
      `${translationsPrefix}.store_opening_hours.label_closed`
    );
  }
  return `${formatTime(day.openMinutes)}\u200a\u2013\u200a${formatTime(day.closeMinutes)}`;
}

/**
 * Get   a translated message saying that the store is opening/closing in a certain time
 * @param {boolean} opening true if store is going to open, false if store is going to close
 * @param {number} timeUntilOpenOrClosed
 * @param {string} translationsPrefix for translations key
 * @returns {string}
 */
function getChangingSoonMessage(opening, timeUntilOpenOrClosed, translationsPrefix) {
  if (!uiSettings.get(`${translationsPrefix}.store_opening_hours.availability_countdown`)) {
    return uiLocale.getString(
      `${translationsPrefix}.store_opening_hours.${opening ? 'opening' : 'closing'}_soon`
    );
  }

  const cutoffTimes = uiSettings.get('stores')['cutoff-time'].sort((a, b) => a - b);
  const displayedTime = cutoffTimes.filter((threshold) => timeUntilOpenOrClosed <= threshold)[0];

  return uiLocale.getFormattedString(
    `${translationsPrefix}.store_opening_hours.${opening ? 'opening' : 'closing'}_soon`,
    { time: displayedTime }
  );
}

/**
 * Get a translated message saying that the store is open
 * @param {boolean} isInsideTable
 * @param {object} todayOpeningHours
 * @param {string} translationsPrefix for translations key
 * @returns {string}
 */
function getOpenMessage(isInsideTable, todayOpeningHours, translationsPrefix) {
  if (isInsideTable) {
    // branch for RNCBasketStoreOpeningHours (currently translation contains "Open now" string)
    return uiLocale.getString(
      `${translationsPrefix}.store_opening_hours.label_open`
    );
  }

  // branch for ProductPageState (currently translation contains "Open today: MM:HH-MM:HH" string)
  return `${uiLocale.getString(
    `${translationsPrefix}.store_opening_hours.open_today`
  )
  } ${formatOpeningHours(todayOpeningHours, translationsPrefix)}`;
}

/**
 * Get a translated message saying that the store is closed
 * @param {boolean} isInsideTable
 * @param {object} nextOpenDay object returned by storeAvailability.getNextOpenDay()
 * @param {string} translationsPrefix for translations key
 * @returns {string}
 */
function getClosedMessage(isInsideTable, nextOpenDay, translationsPrefix) {
  // branch for RNCBasketStoreOpeningHours, no info is shown when store is closed
  if (isInsideTable) {
    return '';
  }

  // the remaining code is for ProductPageState
  const nextDaysTranslationKey = nextOpenDay && [
    `${translationsPrefix}.store_opening_hours.open_today`,
    `${translationsPrefix}.store_opening_hours.open_tomorrow`
  ][nextOpenDay.offset];

  return nextDaysTranslationKey
    ? `${uiLocale.getString(nextDaysTranslationKey)} ${formatOpeningHours(
      nextOpenDay, translationsPrefix
    )}`
    : uiLocale.getString(`${translationsPrefix}.store_opening_hours.opening_hours`);
}

/**
 * Function for displaying the store state message (eg 'Closed', 'Closing soon') based on the
 * current store state
 * @param {StoreAvailability} storeAvailability
 * @param {string} translationsPrefix for translations key
 * @param {boolean} isInsideTable true if the message is used inside OpeningHoursTable component
 *   (used in store finder, basket confirmation), false otherwise
 * @returns {string}
 */
export function getStateMessage(storeAvailability, translationsPrefix, isInsideTable = false) {
  const openState = storeAvailability.getOpenState();
  const todayOpeningHours = storeAvailability.getStoreOpeningHours()[0];
  const nextOpenDay = storeAvailability.getNextOpenDay();
  const timeUntilClosed = storeAvailability.getTimeUntilClosed();
  const timeUntilOpen = storeAvailability.getTimeUntilOpen();

  if (!todayOpeningHours) {
    return '';
  } if (openState === State.OPENING_SOON) {
    return getChangingSoonMessage(true, timeUntilOpen, translationsPrefix);
  } if (openState === State.OPEN) {
    return getOpenMessage(isInsideTable, todayOpeningHours, translationsPrefix);
  } if (openState === State.CLOSING_SOON) {
    return getChangingSoonMessage(false, timeUntilClosed, translationsPrefix);
  } if (openState === State.CLOSED) {
    return getClosedMessage(isInsideTable, nextOpenDay, translationsPrefix);
  }
  return '';
}
